import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API, useApp } from "@/state/AppContext";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function subToJSON(sub) {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    user_agent: navigator.userAgent,
  };
}

export default function usePush() {
  const { token, user, authHeaders } = useApp();
  const [supported] = useState(() =>
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window && "Notification" in window);
  const [permission, setPermission] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const getRegistration = useCallback(async () => {
    if (!supported) return null;
    const existing = await navigator.serviceWorker.getRegistration("/sw.js");
    if (existing) return existing;
    return navigator.serviceWorker.register("/sw.js");
  }, [supported]);

  const refreshState = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await getRegistration();
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch { /* ignore */ }
  }, [supported, getRegistration]);

  useEffect(() => { refreshState(); }, [refreshState]);

  const subscribe = useCallback(async () => {
    if (!supported) throw new Error("Push wird von diesem Browser nicht unterstützt.");
    if (!user) throw new Error("Bitte zuerst anmelden.");
    setBusy(true);
    try {
      const p = Notification.permission === "granted"
        ? "granted"
        : await Notification.requestPermission();
      setPermission(p);
      if (p !== "granted") throw new Error("Benachrichtigungen wurden nicht erlaubt.");
      const cfg = await axios.get(`${API}/push/config`);
      if (!cfg.data?.public_key) throw new Error("Push nicht konfiguriert.");
      const reg = await getRegistration();
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(cfg.data.public_key),
        });
      }
      await axios.post(`${API}/push/subscribe`, subToJSON(sub), { headers: authHeaders });
      setSubscribed(true);
      return true;
    } finally { setBusy(false); }
  }, [supported, user, authHeaders, getRegistration]);

  const unsubscribe = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await getRegistration();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try { await axios.post(`${API}/push/unsubscribe`, subToJSON(sub), { headers: authHeaders }); } catch { /* ignore */ }
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } finally { setBusy(false); }
  }, [authHeaders, getRegistration]);

  const testPush = useCallback(async () => {
    if (!token) return;
    await axios.post(`${API}/push/test`, {}, { headers: authHeaders });
  }, [token, authHeaders]);

  return { supported, permission, subscribed, busy, subscribe, unsubscribe, testPush, refresh: refreshState };
}
